/* global VXConfig */

import React                  from 'react';
import PropTypes              from 'prop-types';
import CtaButton              from './CtaButton';
import BlogPostsStore         from './../../flux/stores/BlogPostsStore';
import Constants              from './../../flux/Constants';
import {openVideoUrlInPlayer} from '../../components/Gallery/VideoPlayer';
import RoundedLabel           from './RoundedLabel';
import CardTitle              from './CardTitle';
import ReloadHelper           from './../../utils/ReloadHelper';
import HistoryStateHelper     from './../../utils/HistoryStateHelper';
import Translations           from '../../utils/Translations';

const BG_RESET_DELAY = 5000;
const BG_COLOR       = '#000';
const BG_SELECTOR    = '.gallery__video-player, .gallery__video-player';

class BlogPostCard extends React.Component {

    constructor(props) {
        super(props);
		this.assignMedia      = this._assignMediaRefHandler.bind(this);
		this.assignBackground = this._assignBackgroundHandler.bind(this);
		this.resetBackground  = this._resetBackgroundHandler.bind(this);
		this.onLinkClick      = this.onLinkClickHandler.bind(this);
		this.onClickImage     = this.onClickImage.bind(this);
	}

	componentDidMount() {
		if (!this.props.video || !this.props.isMobile) {
			return;
		}

		// init video player
		openVideoUrlInPlayer(this.media, this.props.video, [this.props.image], false, false);
		setTimeout(this.resetBackground, BG_RESET_DELAY);
	}

	/**
	 * Prevent clicks on video player to navigate over the link
	 * @param {Event} e
	 */
	onLinkClickHandler(e) {
		// if descendants of video has been clicked
		if (this.props.video.length > 0
			&& this.props.isMobile
			&& this.background.contains(e.target)
		) {
			e.preventDefault();
		}

		const url = this.props.links[VXConfig.language || Constants.Languages.DE];

		if (this.props.isMobile) {
			if (this.props.isMobileFriendly) {
				ReloadHelper.reload(url, e);
			} else {
				e.preventDefault();
				window.location.assign(HistoryStateHelper.urlSetGetParam(url, 'desktoppreview', 1));
				return false;
			}
		} else {
			ReloadHelper.reloadWithPjax(url, e);
		}
	}

	/**
	 * Unset background image
	 * @private
	 */
	_resetBackgroundHandler() {
		// skip if null
		if (null === this.background) {
			return;
		}

		this.background
			.style
			.backgroundImage = '';

		const player = this.background.querySelector(BG_SELECTOR);

		if (null !== player) {
			player.style.backgroundColor = BG_COLOR;
		}
	}

	/**
	 * @return {boolean}
	 * @private
	 */
	_isAction() {
		return !this.props.featured
			&& this.props
				.categories
				.indexOf(Constants.Blog.CategoryIds.ACTIONS) !== -1;
	}

	/**
	 * @link https://reactjs.org/docs/refs-and-the-dom.html
	 * @param {HTMLElement} media
	 * @private
	 */
	_assignMediaRefHandler(media) {
		this.media = media;
	}

	/**
	 * @link https://reactjs.org/docs/refs-and-the-dom.html
	 * @param {HTMLElement} background
	 * @private
	 */
	_assignBackgroundHandler(background) {
		this.background = background;
	}

	/**
	 * @return {*}
	 * @private
	 */
	_renderVideoIcon() {
		// no video in post - no trouble
		if (!this.props.video) {
			return null;
		}

		// on mobile - render video player inline
		if (this.props.isMobile) {
			return (
				<span className="box--fixed-ratio__content">
					<span className="js-tv-media-single-videoplayer" ref={this.assignMedia}>&nbsp;</span>
				</span>
			);
		}

		// just render the icon to lead to details page
		return <i className="icon -icon-play-rounded-line" />;
	}

	onClickImage(e) {
		let url = this.props.links[VXConfig.language || Constants.Languages.DE];

		if (this.props.video) {
			url += '?autoplay=true';
		}

		if (this.props.isMobile) {
			if (this.props.isMobileFriendly) {
				ReloadHelper.reload(url, e);
			} else {
				e.preventDefault();
				window.location.assign(HistoryStateHelper.urlSetGetParam(url, 'desktoppreview', 1));
				return false;
			}
		} else {
			ReloadHelper.reloadWithPjax(url, e);
		}
	}

	/**
	 * @return {String}
	 */
	render() {
		const baseClass  = 'blog-post-card',
		      category   = BlogPostsStore.getCategoryById(this.props.categories.filter(c => c).pop()),
		      url        = this.props.links[VXConfig.language || Constants.Languages.DE];

		const postCardDate = (
			<span className={baseClass + "-meta" + (!this.props.featured ? " timestamp-alignment" : "")}>
				<time className={baseClass + "-meta__date"} dateTime={this.props.date}>
					<i className="icon -icon-calendar">&nbsp;</i>
					{this.props.date}
				</time>
				&nbsp;&#8226;&nbsp;
				<span className={baseClass + "-meta__category"}>{category}</span>
			</span>
		);

		return (
			<a className={baseClass + (!this.props.isMobile ? " magazine-home--post" : "") + " js-pjax-link"}
			   href={!this.props.featured ? url : '#'}
			   data-post-id={this.props.id}
			   onClick={this.onLinkClick}
			   data-index={this.props.index}
			>
				<span onClick={this.onClickImage}
				      className={baseClass + "-image box--fixed-ratio -ratio16_9"}
				      ref={this.assignBackground}
				>
					<div className={baseClass + "-image__wrapper"}>
						<img
							src={this.props.smallImage}
							loading={'lazy'}
						/>
					</div>

					{this._renderVideoIcon()}
					<RoundedLabel text={this.props.label} />
					{this.props.hoverEffect &&
					<span className={"hover-effect " + baseClass + "-image__hover"}>
						<span className="hover-effect_text">
							<i className="icon-actor -icon-award-user-active" />
							{Translations.get('HomePageBlogHoverText')}
							<i className="icon -icon-double-arrow-right-line" />
						</span>
					</span>}
				</span>
				<span className={baseClass + "-short"}>
					<CardTitle text={this.props.title} isAction={this._isAction()} isActionActive={this.props.isActionActive} />
					<h3 className={baseClass + "-title"} dangerouslySetInnerHTML={{__html: this.props.subTitle}} />
					<span className={baseClass + "-excerpt"} dangerouslySetInnerHTML={{__html: this.props.excerpt}} />
					{this.props.featured ? <CtaButton url={url} /> : null}
					{this.props.featured && postCardDate}
				</span>
				{!this.props.featured && postCardDate}
			</a>
		);
	}
}

BlogPostCard.propTypes = {
	id:               PropTypes.number,
	index:            PropTypes.number,
	image:            PropTypes.string,
	smallImage:       PropTypes.string,
	title:            PropTypes.string,
	subTitle:         PropTypes.string,
	excerpt:          PropTypes.string,
	date:             PropTypes.string,
	label:            PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	video:            PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	categories:       PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
	slug:             PropTypes.string,
	featured:         PropTypes.bool,
	isActionActive:   PropTypes.bool,
	isMobile:         PropTypes.bool,
	isMobileFriendly: PropTypes.bool,
	links:            PropTypes.shape({
		[Constants.Languages.EN]: PropTypes.string,
		[Constants.Languages.DE]: PropTypes.string,
		[Constants.Languages.NL]: PropTypes.string,
	}),
	hoverEffect:      PropTypes.bool,
};

BlogPostCard.defaultProps = {
	categories:       [],
	index:            0,
	video:            false,
	featured:         false,
	isActionActive:   false,
	isMobile:         false,
	label:            false,
	links:            {},
	isMobileFriendly: true,
	hoverEffect:      false,
};

export default BlogPostCard;
